import { useState, useEffect } from "react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Calendar, X } from "lucide-react";

/**
 * Parse a date string to a Date object
 * Supports: DD/MM/YYYY (European), YYYY-MM-DD (ISO), DD.MM.YYYY (German)
 * For ambiguous dates (both numbers <= 12), assumes European DD/MM/YYYY
 */
const parseDateValue = (dateStr) => {
  if (!dateStr) return undefined;
  
  const str = String(dateStr).trim();
  
  // Try YYYY-MM-DD format (ISO) - unambiguous
  const yyyymmdd = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (yyyymmdd) {
    return new Date(parseInt(yyyymmdd[1]), parseInt(yyyymmdd[2]) - 1, parseInt(yyyymmdd[3]));
  }
  
  // Try DD.MM.YYYY format (German) - dots always mean DD.MM.YYYY
  const german = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (german) {
    return new Date(parseInt(german[3]), parseInt(german[2]) - 1, parseInt(german[1]));
  }
  
  // Try XX/XX/YYYY or XX-XX-YYYY format
  const slashDash = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (slashDash) {
    const first = parseInt(slashDash[1]);
    const second = parseInt(slashDash[2]);
    const year = parseInt(slashDash[3]);
    
    // If first > 12, it's definitely DD/MM/YYYY
    if (first > 12) {
      return new Date(year, second - 1, first);
    }
    // If second > 12, it's definitely MM/DD/YYYY
    if (second > 12) {
      return new Date(year, first - 1, second);
    }
    // Both <= 12: assume European DD/MM/YYYY
    return new Date(year, second - 1, first);
  }
  
  // Fallback to native Date parsing
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

/**
 * Format a date value for display (DD/MM/YYYY)
 */
const formatDateDisplay = (dateStr) => {
  const date = parseDateValue(dateStr);
  if (!date || isNaN(date.getTime())) return dateStr || "Aucune date";
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function InlineDatePicker({
  isOpen,
  position,
  context,
  onClose,
  onSelect,
  onClear
}) {
  const PICKER_HEIGHT = 400; // Estimated height (header + calendar + footer)
  const PICKER_WIDTH = 300;
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  // Smart positioning: flip above the cell / clamp so the calendar is never clipped by the viewport
  useEffect(() => {
    if (position && isOpen) {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      let newTop = position.top;
      let newLeft = position.left;

      // Not enough room below -> open above the cell
      if (position.top + PICKER_HEIGHT > viewportHeight) {
        const cellHeight = position.cellHeight || 40;
        newTop = position.top - PICKER_HEIGHT - cellHeight - 8;
      }
      // Still overflowing (short viewport) -> clamp within the screen
      if (newTop + PICKER_HEIGHT > viewportHeight) {
        newTop = viewportHeight - PICKER_HEIGHT - 10;
      }
      if (newTop < 10) newTop = 10;

      if (position.left + PICKER_WIDTH > viewportWidth) {
        newLeft = Math.max(10, viewportWidth - PICKER_WIDTH - 10);
      }
      if (newLeft < 10) newLeft = 10;

      setModalPosition({ top: newTop, left: newLeft });
    }
  }, [position?.top, position?.left, position?.cellHeight, isOpen]);

  if (!isOpen) return null;

  const handleDateSelect = (date) => {
    if (date) {
      // Pass the Date object directly to the parent handler
      onSelect(date);
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear(context.orderId, context.columnName);
    }
    // Don't call onClose() here - the clear handler already closes the picker after the API call succeeds
  };

  return (
    <div 
      className="date-picker-dropdown fixed bg-white rounded-lg shadow-xl border border-zinc-200 text-zinc-900 p-3"
      style={{ 
        top: `${modalPosition.top}px`, 
        left: `${modalPosition.left}px`,
        zIndex: 9999 
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-100">
        <span className="font-medium text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-orange-500" />
          {context.columnName}
        </span>
        <button 
          onClick={onClose}
          className="p-1 hover:bg-zinc-100 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <CalendarComponent
        mode="single"
        selected={parseDateValue(context.currentValue)}
        onSelect={handleDateSelect}
        locale={{
          localize: {
            day: n => ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][n],
            month: n => ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][n]
          },
          formatLong: {
            date: () => 'dd/MM/yyyy'
          }
        }}
        className="rounded-md border-0"
      />
      <div className="mt-2 pt-2 border-t border-zinc-100 flex justify-between items-center">
        <span className="text-xs text-zinc-500">
          {formatDateDisplay(context.currentValue)}
        </span>
        <div className="flex gap-2">
          {context.currentValue && (
            <button
              onClick={handleClear}
              className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
            >
              Effacer
            </button>
          )}
          <button
            onClick={() => handleDateSelect(new Date())}
            className="text-xs px-2 py-1 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded"
          >
            Aujourd'hui
          </button>
        </div>
      </div>
    </div>
  );
}
